import { Injectable } from "@angular/core";

import {
    HtmlText,
    HtmlSequence,
    HtmlDirective,
    HtmlExpression,
    HtmlExpressionType,
    HtmlDirectiveEvaluator,
    HtmlDirectiveEvaluatorSettings,
} from "@main/reporting.v3/widget.html/models";


@Injectable()
export class HtmlBaseExpressionsEvaluatorService {

    public normalizeSequence(sequence: HtmlSequence): string {
        if (!sequence?.children?.length) return "";

        // Concat text expressions in this sequence children, to obtain the original input without directives.
        const children: HtmlExpression[] = sequence.children;

        const texts: HtmlText[] = children.filter(child => child instanceof HtmlText) as HtmlText[];

        return texts.map(text => text.text).join("");
    }

    public async evaluate(expression: HtmlExpression, settings: HtmlDirectiveEvaluatorSettings): Promise<any> {
        if (!expression || !settings) return null;

        switch (expression.type) {
            case HtmlExpressionType.Directive:
                const directive: HtmlDirective = expression as HtmlDirective;
                return this.evaluateDirective(directive, settings);

            case HtmlExpressionType.Sequence:
                const sequence: HtmlSequence = expression as HtmlSequence;
                return this.evaluateSequence(sequence, settings);

            case HtmlExpressionType.Text: {
                const text: HtmlText = expression as HtmlText;
                return text?.text ?? "";
            }
        }
    }

    public async evaluateSequence(sequence: HtmlSequence, settings: HtmlDirectiveEvaluatorSettings): Promise<any[]> {
        try {
            if (!sequence || !settings) return null;

            // Resolve children expressions in parallel.
            return await this.evaluateAll(sequence.children, settings);

        } catch (error) {
            return null;
        }
    }

    public async evaluateDirective(directive: HtmlDirective, settings: HtmlDirectiveEvaluatorSettings): Promise<any> {
        try {
            if (!directive?.name || !settings?.evaluators) return null;

            // Get the evaluator corresponding to this directive.
            const evaluator: HtmlDirectiveEvaluator = settings.evaluators[directive.name];

            if (!evaluator) return null;

            // Get the result of the directive.
            return await evaluator.evaluate(directive, settings);

        } catch (error) {
            return null;
        }
    }

    public async evaluateAll(expressions: HtmlExpression[], settings: HtmlDirectiveEvaluatorSettings): Promise<any[]> {
        if (!expressions?.length || !settings) return [];

        return await Promise.all(expressions.map(expression => this.evaluate(expression, settings)));
    }
}